import EntityCard from '@/components/EntityCard';
import { Badge } from '@/components/ui/badge';
import Typography from '@/components/ui/typography';
import { calculatePercentOff } from '@/lib/utils';
import { ProductVariant, ShoppeProduct } from '@/types';

type Variant = Pick<ProductVariant, 'color' | 'images'>;
type Product = Omit<ShoppeProduct<Variant>, 'code' | 'details'>;

const ProductCard = ({ product }: { product: Product }) => {
  const discounted =
    product.actual_price && product.actual_price > product.price;

  return (
    <EntityCard
      href={route('shoppe.showProduct', product.slug)}
      image={`/images/shoppe/products/${product.slug}/${product.variants[0].images[0]}`}
      alt={product.name}
      aspect="square"
      density="compact"
      title={product.name.toLowerCase()}
      // The discount moves onto the image as a flash. It was a third item in
      // the price row, which on a two-up phone grid wrapped the row onto two
      // lines and left the actual price competing with the saving.
      imageBadge={
        discounted ? (
          <Badge className="rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold shadow-none">
            {calculatePercentOff(product.actual_price!, product.price)} off
          </Badge>
        ) : undefined
      }
      footer={
        <div className="flex flex-col gap-2">
          {product.variants.length > 1 && (
            <div className="inline-flex items-center space-x-1.5">
              <div className="inline-flex items-center -space-x-1">
                {product.variants.slice(0, 3).map((variant, index) => (
                  <span
                    key={index}
                    className="size-4 rounded-full border-2 border-card ring-1 ring-border"
                    // Swatch colours are per-product data, so they can only
                    // come through as an inline style.
                    style={{ backgroundColor: variant.color!.value }}
                  />
                ))}
              </div>
              {product.variants.length > 3 && (
                <Typography.Small>
                  +{product.variants.length - 3} more
                </Typography.Small>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-display text-base font-bold tabular-nums tracking-tight">
              &#8377;{product.price}
            </span>
            {discounted && (
              <Typography.Small className="tabular-nums line-through">
                &#8377;{product.actual_price}
              </Typography.Small>
            )}
          </div>
        </div>
      }
    />
  );
};

export default ProductCard;
